import React, { useState, useContext } from "react";
import { graphql } from "gatsby";
import styled from "styled-components";
import { Helmet } from "react-helmet";
import Media from "react-media";
import { use100vh } from "react-div-100vh";

// Previews
import { withPrismicPreview } from "gatsby-plugin-prismic-previews";

// Context
import WindowWidthContext from "../components/context/window-width";

// Components
import { Address } from "../components/layout/address";
import { RelatedExhibitionsByYear } from "../components/layout/related-exhibitions-by-year";
import { Lightbox } from "../components/images/lightbox";
// import { Mailchimp } from "../components/forms/mailchimp";
import { Navigation } from "../components/layout/navigation";

const Page = styled.div`
  padding: 20px;

  & .header {
    max-width: 760px;
  }
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: 1fr 2fr;
  grid-gap: 20px;

  & .left-column {
    & .sticky {
      position: sticky;
      top: 20px;

      min-height: calc(${props => props.height}px - 40px);

      display: flex;
      flex-direction: row;
      flex-wrap: wrap;

      @media (max-width: 767px) {
        display: block;

        min-height: 100%;
      }

      & .content-container {
        width: 100%;
      }
    }
  }

  & .right-column {
    & a {
      text-decoration: underline;
    }
  }

  @media (max-width: 1020px) {
    grid-template-columns: 290px 1fr;
  }

  @media (max-width: 767px) {
    display: block;
    grid-template-columns: unset;
    grid-gap: unset;
  }
`;

const ArtworksContainer = styled.div`
  & ol {
    & li {
      cursor: pointer;

      margin: 0 0 2em 0;
    }
  }
`;

const Exhibition = ({ data, location }) => {
  const [artwork, setArtwork] = useState(null);
  const [isLightboxOpen, setLightBoxOpen] = useState(false);
  const windowWidth = useContext(WindowWidthContext);

  const height = use100vh();

  const artworks = data.prismicExhibition.data.artworks.map(
    (artwork, index) => (
      <li
        key={`single_artwork_${index}_${data.prismicExhibition.id}`}
        className="single-artwork"
      >
        {artwork.image.fluid !== null && (
          <img
            sizes={artwork.image.fluid.sizes}
            srcSet={artwork.image.fluid.srcSet}
            src={artwork.image.fluid.src}
            alt={artwork.image.alt}
            loading={`lazy`}
            onClick={() => {
              setArtwork(artwork);
              setLightBoxOpen(true);
            }}
          />
        )}
      </li>
    )
  );

  return (
    <>
      <Helmet
        title={`${data.prismicExhibition.data.artist.text} – ${data.prismicExhibition.data.title.text} | ${data.prismicSeo.data.seo_title}`}
        meta={[
          {
            name: "title",
            content: `${data.prismicExhibition.data.artist.text} – ${data.prismicExhibition.data.title.text} | ${data.prismicSeo.data.seo_title}`,
          },
          {
            name: "og:title",
            content: `${data.prismicExhibition.data.artist.text} – ${data.prismicExhibition.data.title.text} | ${data.prismicSeo.data.seo_title}`,
          },
          {
            name: "description",
            content: `${data.prismicExhibition.data.exhibition_text.text}`,
          },
          {
            name: "og:description",
            content: `${data.prismicExhibition.data.exhibition_text.text}`,
          },
          {
            name: "twitter:title",
            content: `${data.prismicExhibition.data.artist.text} – ${data.prismicExhibition.data.title.text} | ${data.prismicSeo.data.seo_title}`,
          },
          {
            name: "twitter:description",
            content: `${data.prismicExhibition.data.exhibition_text.text}`,
          },
          {
            property: "og:url",
            content: location.href,
          },
          {
            property: "twitter:url",
            content: location.href,
          },
        ]}
      />
      <Page>
        <Grid height={height}>
          <div className="left-column">
            <div className="sticky">
              <div className="content-container">
                <Address />
                <RelatedExhibitionsByYear
                  activeYear={data.prismicExhibition.data.year}
                />

                <Navigation
                  hideArtists={true}
                  showBackButton={true}
                  year={data.prismicExhibition.data.year}
                />
              </div>
              {/* <Media
                queries={{ medium: "(min-width: 901px)" }}
                defaultMatches={{ medium: windowWidth === 901 }}
                render={() => <Mailchimp />}
              /> */}
            </div>
          </div>

          <div className="right-column">
            <div className="header">
              <div
                className="artist-name"
                dangerouslySetInnerHTML={{
                  __html: data.prismicExhibition.data.artist.html,
                }}
              />
              <div
                className="exhibition-title"
                dangerouslySetInnerHTML={{
                  __html: data.prismicExhibition.data.title.html,
                }}
              />

              <div className="exhibition-dates">
                {data.prismicExhibition.data.start_date}
                {" – "}
                {data.prismicExhibition.data.end_date}
              </div>
              <br />
              <div
                className="exhibition-text"
                dangerouslySetInnerHTML={{
                  __html: data.prismicExhibition.data.exhibition_text.html,
                }}
              />
            </div>

            <ArtworksContainer>
              <ol>{artworks}</ol>
            </ArtworksContainer>
          </div>

          {/* <Media
            queries={{ medium: "(max-width: 900px)" }}
            defaultMatches={{ medium: windowWidth === 900 }}
            render={() => (
              <div className="mobile-mailchimp-container">
                <Mailchimp />
              </div>
            )}
          /> */}
        </Grid>
      </Page>

      {isLightboxOpen && (
        <Lightbox
          artist={data.prismicExhibition.data.artist}
          artwork={artwork}
          isLightboxOpen={isLightboxOpen}
          setLightBoxOpen={setLightBoxOpen}
        />
      )}

      <form
        hidden
        name="artwork-enquiry"
        data-netlify="true"
        data-netlify-honeypot="bot-field"
      >
        <input type="hidden" name="bot-field" />
        <input type="hidden" name="form-name" value="artwork-enquiry" />
        {data.prismicExhibition.data.artworks.map((artwork, index) => (
          <input
            key={`single_hidden_subject_${data.prismicExhibition.data.artist.text}_${index}`}
            type="hidden"
            name="subject"
            value={`${data.prismicExhibition.data.artist.text} – ${artwork.artwork_title.text} Enquiry`}
          />
        ))}

        <input type="hidden" name="email" />
      </form>
    </>
  );
};

export default withPrismicPreview(Exhibition);

export const query = graphql`
  query Exhibition($uid: String!) {
    prismicSeo {
      data {
        seo_title
      }
    }
    prismicExhibition(uid: { eq: $uid }) {
      id
      data {
        artist {
          html
          text
        }
        title {
          html
          text
        }
        year: start_date(formatString: "YYYY")
        start_date(formatString: "DD MMMM YY")
        end_date(formatString: "DD MMMM YY")
        exhibition_text {
          html
          text
        }
        artworks {
          image {
            alt
            dimensions {
              height
              width
            }
            fluid(
              imgixParams: { auto: "format" }
              srcSetBreakpoints: [1400, 1600, 2400, 3200, 3600]
            ) {
              src
              srcSet
              sizes
            }
          }
          artwork_title {
            text
            html
          }
          artwork_text {
            html
          }
        }
      }
    }
  }
`;
